import { useState } from 'react';
import usePageTitle from '../hooks/usePageTitle';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2, Plus, Phone, MapPin, FileText } from 'lucide-react';
import { getSourcers, createSourcer, updateSourcer, deleteSourcer } from '../api/sourcers';
import Modal from '../components/Modal';
import Pagination from '../components/Pagination';
import FilterBar from '../components/FilterBar';
import { useAuth } from '../context/AuthContext';
import { canWrite } from '../utils/permissions';
import { useTranslation } from 'react-i18next';

const EMPTY_FORM = { name: '', phone: '', address: '', notes: '' };

export default function Sourcers() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  usePageTitle('Sourcers');
  const { t } = useTranslation();
  const { user } = useAuth();
  const write = canWrite(user?.role);
  const [filters, setFilters] = useState({});
  const handleFilter = (key, val) => { setFilters((f) => ({ ...f, [key]: val || undefined })); setPage(0); };
  const clearFilters = () => { setFilters({}); setPage(0); };
  const { data, isLoading } = useQuery({
    queryKey: ['sourcers', page, filters],
    queryFn: () => getSourcers(page, 10, filters).then((r) => r.data.data),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['sourcers'] });

  const saveMutation = useMutation({
    mutationFn: (payload) =>
      modal?.mode === 'edit' ? updateSourcer(modal.data.id, payload) : createSourcer(payload),
    onSuccess: () => { invalidate(); closeModal(); },
    onError: (err) => setFormError(err.response?.data?.message || 'Something went wrong.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteSourcer(id),
    onSuccess: () => { invalidate(); setDeleteTarget(null); },
  });

  const openCreate = () => { setForm(EMPTY_FORM); setFormError(''); setModal({ mode: 'create' }); };
  const openEdit = (s) => {
    setForm({ name: s.name, phone: s.phone || '', address: s.address || '', notes: s.notes || '' });
    setFormError('');
    setModal({ mode: 'edit', data: s });
  };
  const closeModal = () => setModal(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError('');
    saveMutation.mutate({
      name: form.name,
      phone: form.phone || null,
      address: form.address || null,
      notes: form.notes || null,
    });
  };

  const sourcers = data?.content ?? [];
  const totalPages = data?.totalPages ?? 0;

  return (
    <div className="p-6 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">{t('sourcers.title')}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{data?.totalElements ?? 0} {t('common.total')}</p>
        </div>
        {write && (
          <button onClick={openCreate} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
            <Plus size={15} /> {t('sourcers.new')}
          </button>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="py-16 text-center text-sm text-gray-400">{t('common.loading')}</div>
        ) : sourcers.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-400">{t('sourcers.noSourcers')}</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50 text-left">
                <th className="px-4 py-3 font-medium text-gray-500">{t('common.name')}</th>
                <th className="px-4 py-3 font-medium text-gray-500">{t('common.phone')}</th>
                <th className="px-4 py-3 font-medium text-gray-500">{t('common.address')}</th>
                <th className="px-4 py-3 w-20" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sourcers.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-800 flex items-center gap-2">
                      {s.name}
                      {s.isDefault && <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">{t('sourcers.default')}</span>}
                    </p>
                    {s.notes && <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{s.notes}</p>}
                  </td>
                  <td className="px-4 py-3 text-gray-500">
                    {s.phone
                      ? <span className="flex items-center gap-1.5"><Phone size={12} className="text-gray-400" />{s.phone}</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs max-w-[200px]">
                    {s.address ? <span className="truncate block">{s.address}</span> : <span className="text-gray-300">—</span>}
                  </td>
                  {write && (
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => openEdit(s)} className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"><Pencil size={14} /></button>
                        {!s.isDefault && <button onClick={() => setDeleteTarget(s)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"><Trash2 size={14} /></button>}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />

      {modal && (
        <Modal title={modal.mode === 'create' ? t('sourcers.new') : t('common.edit') + ' ' + t('sourcers.title')} onClose={closeModal}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.name')} <span className="text-red-500">*</span></label>
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required autoFocus
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('sourcers.namePlaceholder')} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1"><span className="flex items-center gap-1.5"><Phone size={13} /> {t('common.phone')}</span></label>
              <input type="text" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder={t('common.optional')} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1"><span className="flex items-center gap-1.5"><MapPin size={13} /> {t('common.address')}</span></label>
              <textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder={t('common.optional')} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1"><span className="flex items-center gap-1.5"><FileText size={13} /> {t('common.notes')}</span></label>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder={t('common.optional')} />
            </div>
            {formError && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{formError}</p>}
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={closeModal} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">{t('common.cancel')}</button>
              <button type="submit" disabled={saveMutation.isPending} className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg transition-colors">
                {saveMutation.isPending ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {deleteTarget && (
        <Modal title={t('sourcers.deleteSourcer')} onClose={() => setDeleteTarget(null)}>
          <p className="text-sm text-gray-600"><span className="font-medium text-gray-900">{deleteTarget.name}</span>? {t('common.cannotBeUndone')}</p>
          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">{t('common.cancel')}</button>
            <button onClick={() => deleteMutation.mutate(deleteTarget.id)} disabled={deleteMutation.isPending}
              className="px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg transition-colors">
              {deleteMutation.isPending ? t('common.deleting') : t('common.delete')}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
